import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { paymentsApi, studentsApi, configApi } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CurrencySelector from '../components/CurrencySelector';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MultiSelect } from '../components/MultiSelect';
import { 
  Plus, Search, ChevronLeft, ChevronRight, Loader2, Download, AlertCircle, Pencil, Lock, Link2
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { useBulkSelect, BulkDeleteButton, BulkSelectionBar } from '../components/BulkDelete';
import BankSelect from '../components/BankSelect';
import InvoiceMappingEditor from '../components/InvoiceMappingEditor';
import { toast } from 'sonner';

const formatCurrency = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return '-';
  return `${currency} ${Number(amount).toLocaleString()}`;
};

// Money on a receipt that is not tied to any invoice yet. It still counts as
// collected (Term Financials books money by the date received), but the
// student's invoices keep showing as unpaid until someone allocates it.
const unallocatedOf = (p) => Number(p?.unallocated_amount || 0);

// Dues clear oldest term first: Term 1 -> Term 2 -> Term 3, older academic year
// before newer. Ordering on created_at alone put a later term above an unpaid
// earlier term whenever invoices were raised out of term order (e.g. a Term-1
// invoice regenerated after the Term-3 ones). Mirrors settlement_order_key on
// the backend, which allocates the money in this same order.
const TERM_RANK = { 'Term 1': 0, 'Term 2': 1, 'Term 3': 2 };

const settlementOrder = (feeHeadPriority = {}) => (a, b) => (
  String(a.academic_year || '').localeCompare(String(b.academic_year || '')) ||
  (TERM_RANK[a.term] ?? 99) - (TERM_RANK[b.term] ?? 99) ||
  (feeHeadPriority[a.fee_head] ?? 999) - (feeHeadPriority[b.fee_head] ?? 999) ||
  String(a.due_date || '9999-12-31').localeCompare(String(b.due_date || '9999-12-31')) ||
  String(a.created_at || '').localeCompare(String(b.created_at || ''))
);

// The academic period today falls in. Mirrors period_for_date() in
// backend/config.py: the year rolls over on the Term-1 start date (1 July), so
// 11 Aug 2026 is "2026-2027 / Term 1". Used only to badge the current term in
// the invoice picker — staff reported the current invoice "not appearing" when
// it was simply sorted below the older terms and scrolled out of view.
const TERM_CALENDAR = [['Term 1', 7, 1], ['Term 2', 10, 1], ['Term 3', 2, 1]];

const monthDay = (m, d) => m * 100 + d;
const yearMonthDay = (y, m, d) => y * 10000 + m * 100 + d;

const currentPeriod = (now = new Date()) => {
  const [, startMonth, startDay] = TERM_CALENDAR[0];
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const d = now.getDate();
  const startYear = monthDay(m, d) >= monthDay(startMonth, startDay) ? y : y - 1;
  let term = TERM_CALENDAR[0][0];
  for (const [name, month, day] of TERM_CALENDAR) {
    // Terms after the year-start boundary sit in the first calendar year;
    // terms before it (e.g. Term 3 in Feb) sit in the second.
    const calYear = monthDay(month, day) >= monthDay(startMonth, startDay) ? startYear : startYear + 1;
    if (yearMonthDay(y, m, d) >= yearMonthDay(calYear, month, day)) term = name;
  }
  return { academic_year: `${startYear}-${startYear + 1}`, term };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const PaymentsPage = () => {
  const { isAdmin, userBranches } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [modeFilter, setModeFilter] = useState([]);
  const [allocationFilter, setAllocationFilter] = useState('all');
  const [unallocSummary, setUnallocSummary] = useState(null);
  // Receipt whose leftover money is being put against invoices, straight from
  // the list — the same editor (and endpoint) as Edit > Invoice Mapping.
  const [allocatePayment, setAllocatePayment] = useState(null);
  const [branchFilter, setBranchFilter] = useState([]);
  const [classFilter, setClassFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentResults, setStudentResults] = useState([]);
  const [searchingStudent, setSearchingStudent] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentOutstanding, setStudentOutstanding] = useState(null);
  const [outstandingInvoices, setOutstandingInvoices] = useState([]); // pending/partial invoices for the picker
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);   // invoices the payment is for
  const invoiceListRef = useRef(null);
  const [invoiceListScrolls, setInvoiceListScrolls] = useState(false); // list taller than its box
  const [loadingOutstanding, setLoadingOutstanding] = useState(false);

  const sel = useBulkSelect();
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${sel.size} selected payment(s)? Their allocations to invoices will be reversed (those invoices revert to unpaid/partial). This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await paymentsApi.bulkDelete(Array.from(sel.selected));
      toast.success(res.data.message || 'Deleted');
      sel.clear();
      fetchPayments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setBulkDeleting(false);
    }
  };

  const [newPayment, setNewPayment] = useState({
    student_id: '', amount: '', currency: 'USD',
    payment_mode: 'cash', reference_number: '', bank_name: '', notes: '', effective_date: ''
  });

  useEffect(() => {
    configApi.get().then(r => setConfig(r.data)).catch(() => {});
  }, []);

  // Modes THIS user may record in — the accountant assistant is bank-only, and the
  // backend rejects anything else, so the form must not offer what would 403.
  // Comes from /api/config so no role name is hard-coded on the client.
  const modeLabels = { cash: 'Cash', bank_deposit: 'Bank Deposit' };
  const allowedModes = useMemo(
    () => config?.allowed_payment_modes || ['cash', 'bank_deposit'],
    [config]);
  const canRecordCash = allowedModes.includes('cash');

  // Keep the form on a mode the user is allowed to use. The default is 'cash', so
  // a bank-only user would otherwise open the dialog already invalid.
  useEffect(() => {
    if (allowedModes.length && !allowedModes.includes(newPayment.payment_mode)) {
      setNewPayment((p) => ({ ...p, payment_mode: allowedModes[0] }));
    }
  }, [allowedModes, newPayment.payment_mode]);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (modeFilter.length) params.payment_mode = modeFilter.join(',');
      if (branchFilter.length) params.branch = branchFilter.join(',');
      if (classFilter.length) params.current_class = classFilter.join(',');
      if (searchQuery) params.search = searchQuery;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (allocationFilter !== 'all') params.allocation = allocationFilter;

      const response = await paymentsApi.getAll(params);
      setPayments(response.data.payments);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
      setUnallocSummary(response.data.unallocated || null);
    } catch (err) {
      toast.error('Failed to load payments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, modeFilter, branchFilter, classFilter, searchQuery, startDate, endDate, allocationFilter]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const searchStudents = async (query) => {
    if (!query || query.length < 2) { setStudentResults([]); return; }
    setSearchingStudent(true);
    try {
      const response = await studentsApi.getAll({ search: query, limit: 10 });
      setStudentResults(response.data.students);
    } catch (err) { console.error(err); }
    finally { setSearchingStudent(false); }
  };

  const fetchOutstanding = async (studentId) => {
    setLoadingOutstanding(true);
    try {
      const [outRes, invRes] = await Promise.all([
        api.get(`/students/${studentId}/outstanding`),
        api.get('/invoices', { params: { student_id: studentId, status: 'pending,partial', limit: 200 } }),
      ]);
      setStudentOutstanding(outRes.data);
      const invs = (invRes.data?.invoices || [])
        .filter((i) => (Number(i.outstanding) || 0) > 0)
        .sort(settlementOrder(config?.fee_head_priority));
      setOutstandingInvoices(invs);
    } catch (err) {
      console.error('Failed to fetch outstanding:', err);
      setStudentOutstanding(null);
      setOutstandingInvoices([]);
    } finally {
      setLoadingOutstanding(false);
    }
  };

  // Toggle an invoice in the selection and auto-fill the amount from the picks.
  const toggleInvoice = (inv) => {
    const isSel = selectedInvoiceIds.includes(inv.id);
    const nextIds = isSel ? selectedInvoiceIds.filter((x) => x !== inv.id) : [...selectedInvoiceIds, inv.id];
    setSelectedInvoiceIds(nextIds);
    const picked = outstandingInvoices.filter((i) => nextIds.includes(i.id));
    const sum = picked.reduce((s, i) => s + (Number(i.outstanding) || 0), 0);
    const currencies = [...new Set(picked.map((i) => i.currency || 'USD'))];
    setNewPayment((p) => ({
      ...p,
      amount: nextIds.length ? String(Number(sum.toFixed(2))) : '',
      currency: currencies.length === 1 ? currencies[0] : p.currency,
    }));
  };

  // Flag a skipped term: an unticked invoice from a term earlier than one that IS
  // ticked. Warning only, not a block — staff occasionally need to settle a
  // specific invoice. Compared term-wise, so picking within a single term is fine.
  const termKey = (i) => `${i.academic_year || ''}|${TERM_RANK[i.term] ?? 99}`;
  const pickedTermKeys = outstandingInvoices
    .filter((i) => selectedInvoiceIds.includes(i.id))
    .map(termKey);
  const skippedEarlierTerm = pickedTermKeys.length
    ? outstandingInvoices.find((i) =>
        !selectedInvoiceIds.includes(i.id) && pickedTermKeys.some((k) => termKey(i) < k))
    : null;

  // The picker is ordered oldest term first, so the CURRENT term always sits at
  // the bottom. Badge it, and tell the user when it isn't in the list at all —
  // "current invoice missing" usually means no invoice was raised for the term,
  // not that the picker dropped it.
  const nowPeriod = currentPeriod();
  const isCurrentTerm = (i) =>
    i.academic_year === nowPeriod.academic_year && i.term === nowPeriod.term;
  const hasCurrentTermInvoice = outstandingInvoices.some(isCurrentTerm);

  // Rows hidden below the fold read as "not appearing" — measure and say so.
  useEffect(() => {
    const el = invoiceListRef.current;
    setInvoiceListScrolls(!!el && el.scrollHeight > el.clientHeight + 1);
  }, [outstandingInvoices]);

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setNewPayment({ ...newPayment, student_id: student.id });
    setSelectedInvoiceIds([]);
    setOutstandingInvoices([]);
    setStudentResults([]);
    setStudentSearch('');
    fetchOutstanding(student.id);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!newPayment.student_id) { toast.error('Please select a student'); return; }
    if (outstandingInvoices.length > 0 && selectedInvoiceIds.length === 0) {
      toast.error('Select at least one invoice this payment is for');
      return;
    }

    const paymentAmount = parseFloat(newPayment.amount);

    setSubmitting(true);
    try {
      await paymentsApi.create({
        student_id: newPayment.student_id,
        invoice_ids: selectedInvoiceIds.length ? selectedInvoiceIds : null,
        amount: paymentAmount,
        currency: newPayment.currency,
        payment_mode: newPayment.payment_mode,
        reference_number: newPayment.reference_number || null,
        bank_name: newPayment.bank_name || null,
        notes: newPayment.notes || null,
        effective_date: newPayment.payment_mode === 'bank_deposit' ? (newPayment.effective_date || null) : null
      });
      toast.success('Payment recorded successfully');
      setShowAddDialog(false);
      setNewPayment({ student_id: '', amount: '', currency: 'UGX', payment_mode: allowedModes[0] || 'cash', reference_number: '', bank_name: '', notes: '', effective_date: '' });
      setSelectedStudent(null);
      setStudentOutstanding(null);
      setOutstandingInvoices([]);
      setSelectedInvoiceIds([]);
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadReceiptPdf = async (payment) => {
    try {
      toast.loading('Generating PDF...', { id: 'pdf-download' });
      const response = await api.get(`/payments/${payment.id}/receipt-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${payment.receipt_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded', { id: 'pdf-download' });
    } catch (err) {
      toast.error('Failed to download receipt', { id: 'pdf-download' });
      console.error(err);
    }
  };

  const amountExceedsOutstanding = false; // Overpayments allowed

  // Edit state
  const [editPayment, setEditPayment] = useState(null);
  const [editPayData, setEditPayData] = useState({});
  const [editPaySubmitting, setEditPaySubmitting] = useState(false);

  // Persist the form fields without closing the dialog. The invoice-mapping editor
  // calls this before re-spreading the money, so a freshly typed amount is saved
  // first and the invoices get the new figure rather than the stale one.
  const saveEditPayFields = async () => {
    if (!editPayment) return;
    await api.put(`/payments/${editPayment.id}`, editPayData);
    setEditPayment((p) => (p ? { ...p, ...editPayData } : p));
    fetchPayments();
  };

  const handleEditPaySave = async () => {
    if (!editPayment) return;
    setEditPaySubmitting(true);
    try {
      await api.put(`/payments/${editPayment.id}`, editPayData);
      toast.success('Payment updated');
      setEditPayment(null);
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update payment');
    } finally {
      setEditPaySubmitting(false);
    }
  };

  const openPayEdit = (pay) => {
    setEditPayment(pay);
    setEditPayData({
      amount: pay.amount,
      currency: pay.currency || 'USD',
      payment_mode: pay.payment_mode || 'cash',
      reference_number: pay.reference_number || '',
      bank_name: pay.bank_name || '',
      notes: pay.notes || '',
      effective_date: pay.effective_date || ''
    });
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">Payments</h1>
          <p className="text-[#5C635F] mt-1">{total} payments recorded</p>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) { setSelectedStudent(null); setStudentOutstanding(null); setOutstandingInvoices([]); setSelectedInvoiceIds([]); }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="add-payment-btn">
              <Plus className="w-4 h-4 mr-2" />Record Payment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Record New Payment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddPayment} className="space-y-4">
              {/* Student Search */}
              <div className="space-y-2">
                <Label>Student</Label>
                {selectedStudent ? (
                  <div className="flex items-center justify-between p-3 bg-[#F5F5FA] rounded-lg">
                    <div>
                      <p className="font-medium">{selectedStudent.first_name} {selectedStudent.last_name}</p>
                      <p className="text-sm text-[#5C635F]">{selectedStudent.student_id} - {selectedStudent.branch}</p>
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => {
                      setSelectedStudent(null);
                      setStudentOutstanding(null);
                      setOutstandingInvoices([]);
                      setSelectedInvoiceIds([]);
                      setNewPayment({ ...newPayment, student_id: '', amount: '' });
                    }}>Change</Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
                    <Input
                      placeholder="Search student by name or ID..."
                      value={studentSearch}
                      onChange={(e) => { setStudentSearch(e.target.value); searchStudents(e.target.value); }}
                      className="pl-9"
                      data-testid="payment-student-search"
                    />
                    {searchingStudent && <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-[#5C635F]" />}
                    {studentResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-[#D8D9E8] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {studentResults.map((student) => (
                          <button key={student.id} type="button"
                            className="w-full px-3 py-2 text-left hover:bg-[#F5F5FA] flex items-center justify-between"
                            onClick={() => handleStudentSelect(student)}
                          >
                            <span className="font-medium">{student.first_name} {student.last_name}</span>
                            <span className="text-sm text-[#5C635F]">{student.student_id}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Outstanding Balance Display */}
              {selectedStudent && (
                <div className={`p-3 rounded-lg border ${
                  loadingOutstanding ? 'bg-gray-50 border-gray-200' :
                  studentOutstanding?.total_outstanding > 0 ? 'bg-[#C85A40]/10 border-[#C85A40]/20' : 'bg-[#5556A8]/10 border-[#5556A8]/20'
                }`} data-testid="student-outstanding-display">
                  {loadingOutstanding ? (
                    <div className="flex items-center gap-2 text-sm text-[#5C635F]">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading outstanding...
                    </div>
                  ) : studentOutstanding ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase font-semibold text-[#5C635F]">Outstanding Balance</p>
                        <p className={`text-lg font-bold tabular-nums ${studentOutstanding.total_outstanding > 0 ? 'text-[#C85A40]' : 'text-[#5556A8]'}`} data-testid="outstanding-amount">
                          {formatCurrency(studentOutstanding.total_outstanding)}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {studentOutstanding.invoice_count} invoice{studentOutstanding.invoice_count !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  ) : (
                    <p className="text-sm text-[#5556A8]">No outstanding invoices</p>
                  )}
                </div>
              )}

              {/* Invoice selection — pick which invoice(s) this payment is for. The
                  receipt lists only these, so parents don't see unrelated invoices. */}
              {selectedStudent && outstandingInvoices.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>
                      Pay for invoice(s){' '}
                      <span className="font-normal text-xs text-[#5C635F]">
                        — {outstandingInvoices.length} outstanding, oldest term first
                      </span>
                    </Label>
                    <button
                      type="button"
                      className="text-xs text-[#5556A8] hover:underline"
                      onClick={() => {
                        const allIds = outstandingInvoices.map((i) => i.id);
                        const allSel = selectedInvoiceIds.length === allIds.length;
                        setSelectedInvoiceIds(allSel ? [] : allIds);
                        const picked = allSel ? [] : outstandingInvoices;
                        const sum = picked.reduce((s, i) => s + (Number(i.outstanding) || 0), 0);
                        const currencies = [...new Set(picked.map((i) => i.currency || 'USD'))];
                        setNewPayment((p) => ({
                          ...p,
                          amount: picked.length ? String(Number(sum.toFixed(2))) : '',
                          currency: currencies.length === 1 ? currencies[0] : p.currency,
                        }));
                      }}
                    >
                      {selectedInvoiceIds.length === outstandingInvoices.length ? 'Clear all' : 'Select all'}
                    </button>
                  </div>
                  <div
                    ref={invoiceListRef}
                    className="max-h-96 overflow-y-auto rounded-lg border border-[#D8D9E8] divide-y divide-[#EEF]"
                  >
                    {outstandingInvoices.map((inv) => (
                      <label
                        key={inv.id}
                        className={`flex items-center gap-2 px-3 py-2 hover:bg-[#F5F5FA] cursor-pointer text-sm ${
                          isCurrentTerm(inv) ? 'bg-[#3E3F96]/5' : ''
                        }`}
                        data-testid={`pay-invoice-${inv.id}`}
                      >
                        <Checkbox
                          checked={selectedInvoiceIds.includes(inv.id)}
                          onCheckedChange={() => toggleInvoice(inv)}
                        />
                        <span className="flex-1 truncate">
                          <span className="font-medium">{inv.invoice_number}</span>
                          <span className="text-[#5C635F]"> · {inv.term || ''} {inv.academic_year || ''}</span>
                          {isCurrentTerm(inv) && (
                            <span className="ml-2 text-[10px] uppercase font-semibold tracking-wide text-[#3E3F96] bg-[#3E3F96]/10 rounded px-1.5 py-0.5">
                              Current
                            </span>
                          )}
                        </span>
                        <span className="tabular-nums text-[#C85A40] font-medium">
                          {formatCurrency(inv.outstanding, inv.currency)}
                        </span>
                      </label>
                    ))}
                  </div>
                  {invoiceListScrolls && (
                    <p className="text-xs text-[#5C635F]" data-testid="invoice-list-scroll-hint">
                      Scroll the list — the current term is at the bottom.
                    </p>
                  )}
                  {!hasCurrentTermInvoice && (
                    <p className="text-xs text-[#C85A40] flex items-start gap-1" data-testid="no-current-term-invoice">
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>
                        No outstanding {nowPeriod.term} {nowPeriod.academic_year} invoice for this student —
                        nothing has been raised for the current term, or it is already settled.
                        Raise the invoice first if this payment is for {nowPeriod.term}.
                      </span>
                    </p>
                  )}
                  {skippedEarlierTerm && (
                    <p className="text-xs text-[#C85A40] flex items-start gap-1" data-testid="earlier-term-warning">
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>
                        {skippedEarlierTerm.term} {skippedEarlierTerm.academic_year} ({skippedEarlierTerm.invoice_number})
                        is still outstanding. Clear the earlier term before paying a later one.
                      </span>
                    </p>
                  )}
                  <p className="text-xs text-[#5C635F]">
                    Only the ticked invoices are paid and shown on the receipt. Amount is auto-filled from your selection (editable).
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input
                    type="number"
                    value={newPayment.amount}
                    onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                    required min="0" step="0.01"
                    data-testid="payment-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <CurrencySelector value={newPayment.currency} onChange={(v) => setNewPayment({ ...newPayment, currency: v })} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Payment Mode</Label>
                <Select value={newPayment.payment_mode} onValueChange={(v) => setNewPayment({ ...newPayment, payment_mode: v })}
                  disabled={allowedModes.length < 2}>
                  <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allowedModes.map((m) => (
                      <SelectItem key={m} value={m}>{modeLabels[m] || m.replace(/_/g, ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!canRecordCash && (
                  <p className="text-xs text-[#5C635F]" data-testid="payment-mode-restricted">
                    Your role records bank payments only — cash receipts are keyed by the
                    cashier.
                  </p>
                )}
              </div>

              {newPayment.payment_mode === 'bank_deposit' && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Bank Name</Label>
                    <BankSelect value={newPayment.bank_name} onChange={(v) => setNewPayment({ ...newPayment, bank_name: v })} testId="payment-bank" />
                  </div>
                  <div className="space-y-2">
                    <Label>Reference #</Label>
                    <Input value={newPayment.reference_number} onChange={(e) => setNewPayment({ ...newPayment, reference_number: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Effective Date</Label>
                    <Input 
                      type="date" 
                      value={newPayment.effective_date} 
                      onChange={(e) => setNewPayment({ ...newPayment, effective_date: e.target.value })}
                      data-testid="payment-effective-date"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
                  disabled={submitting || amountExceedsOutstanding}
                  data-testid="submit-payment-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Record Payment
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* First row - Search and Branch/Class */}
            <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
              <div className="relative flex-1 min-w-48">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
                <Input
                  placeholder="Search by student name or ID..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="pl-9"
                  data-testid="payment-search"
                />
              </div>
              <MultiSelect widthClass="w-full sm:w-36" placeholder="All Branches" testId="branch-filter"
                options={(isAdmin ? config?.branches : userBranches) || []}
                selected={branchFilter} onChange={(v) => { setBranchFilter(v); setPage(1); }} />
              <MultiSelect widthClass="w-full sm:w-36" placeholder="All Classes" testId="class-filter"
                options={config?.classes || []}
                selected={classFilter} onChange={(v) => { setClassFilter(v); setPage(1); }} />
            </div>
            {/* Second row - Mode, Dates */}
            <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
              <MultiSelect widthClass="w-full sm:w-40" placeholder="All Modes" testId="mode-filter"
                options={[{ value: 'cash', label: 'Cash' }, { value: 'bank_deposit', label: 'Bank Deposit' }]}
                selected={modeFilter} onChange={(v) => { setModeFilter(v); setPage(1); }} />
              <Select value={allocationFilter} onValueChange={(v) => { setAllocationFilter(v); setPage(1); }}>
                <SelectTrigger className="w-full sm:w-52" data-testid="allocation-filter">
                  <SelectValue placeholder="All payments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All payments</SelectItem>
                  <SelectItem value="unallocated">Unallocated only</SelectItem>
                  <SelectItem value="allocated">Fully allocated</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                  className="w-36"
                  data-testid="payment-start-date"
                />
                <span className="text-[#5C635F] text-sm">to</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                  className="w-36"
                  data-testid="payment-end-date"
                />
              </div>
              {(searchQuery || branchFilter.length || classFilter.length || startDate || endDate
                || allocationFilter !== 'all') && (
                <Button variant="ghost" size="sm" className="text-[#5C635F]" onClick={() => { 
                  setSearchQuery(''); setBranchFilter([]); setClassFilter([]); 
                  setStartDate(''); setEndDate(''); setAllocationFilter('all'); setPage(1); 
                }}>
                  Clear filters
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Money sitting on receipts that nobody has tied to an invoice yet. Built
          from the other filters, so the figure does not move when the allocation
          filter is switched on and off. */}
      {unallocSummary?.count > 0 && (
        <Card className="border-[#D99C3B]/40 bg-[#D99C3B]/5">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-[#D99C3B] shrink-0" />
              <div>
                <p className="font-medium text-[#1C1F1D]">
                  {formatCurrency(unallocSummary.total_usd, 'USD')} on {unallocSummary.count} receipt
                  {unallocSummary.count === 1 ? '' : 's'} is not allocated to any invoice
                </p>
                <p className="text-xs text-[#5C635F]">
                  {Object.entries(unallocSummary.by_currency || {})
                    .map(([c, amt]) => `${c} ${Number(amt).toLocaleString()}`).join(' · ')}
                  {' · counted as collected, but the invoices stay open until it is allocated'}
                </p>
              </div>
            </div>
            <Button
              variant={allocationFilter === 'unallocated' ? 'default' : 'outline'}
              size="sm"
              className={allocationFilter === 'unallocated' ? 'bg-[#3E3F96] hover:bg-[#2D2E7A]' : ''}
              onClick={() => {
                setAllocationFilter(allocationFilter === 'unallocated' ? 'all' : 'unallocated');
                setPage(1);
              }}
              data-testid="unallocated-toggle"
            >
              {allocationFilter === 'unallocated' ? 'Show all payments' : 'Show these receipts'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Floats over the page — selecting a row must not shift the table. */}
      {isAdmin && (
        <BulkSelectionBar count={sel.size} onClear={sel.clear}>
          <BulkDeleteButton count={sel.size} deleting={bulkDeleting} onClick={handleBulkDelete} label="payments" />
        </BulkSelectionBar>
      )}

      {/* Payments table */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" />
            </div>
          ) : payments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#5C635F]">
              <p>No payments found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F5FA] hover:bg-[#F5F5FA]">
                    {isAdmin && (
                      <TableHead className="w-8">
                        <Checkbox
                          checked={payments.length > 0 && payments.every((p) => sel.isSelected(p.id))}
                          onCheckedChange={() => sel.toggleAll(payments.map((p) => p.id))}
                          aria-label="Select all"
                        />
                      </TableHead>
                    )}
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Receipt #</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Date</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Student</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Mode</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Effective Date</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Amount</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Unallocated</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id} data-testid={`payment-row-${payment.receipt_number}`}>
                      {isAdmin && (
                        <TableCell>
                          <Checkbox checked={sel.isSelected(payment.id)} onCheckedChange={() => sel.toggle(payment.id)} aria-label="Select row" />
                        </TableCell>
                      )}
                      <TableCell className="font-medium text-[#3E3F96]">{payment.receipt_number}</TableCell>
                      <TableCell className="text-[#5C635F]">{formatDate(payment.created_at)}</TableCell>
                      <TableCell>
                        <p className="font-medium">{payment.student_name}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{payment.payment_mode?.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-[#5C635F] text-sm">
                        {payment.payment_mode === 'bank_deposit' && payment.effective_date
                          ? new Date(payment.effective_date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums text-[#5556A8]">
                        {formatCurrency(payment.amount, payment.currency)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {unallocatedOf(payment) > 0 ? (
                          <>
                            <span className="font-medium text-[#D99C3B]">
                              {formatCurrency(unallocatedOf(payment), payment.currency)}
                            </span>
                            <span className="block text-[10px] text-[#5C635F]">
                              {payment.student_id ? 'to allocate' : 'imported opening balance'}
                            </span>
                          </>
                        ) : (
                          <span className="text-[#5C635F] text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {isAdmin && (
                            payment.tally_posted ? (
                              <Button variant="ghost" size="sm" disabled title="Posted to Tally - Locked"><Lock className="w-4 h-4 text-[#999]" /></Button>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={() => openPayEdit(payment)} title="Edit" data-testid={`edit-payment-${payment.receipt_number}`}><Pencil className="w-4 h-4 text-[#3E3F96]" /></Button>
                            )
                          )}
                          {isAdmin && unallocatedOf(payment) > 0 && payment.student_id && !payment.tally_posted && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setAllocatePayment(payment)}
                              title={`Allocate ${formatCurrency(unallocatedOf(payment), payment.currency)} to invoices`}
                              data-testid={`allocate-payment-${payment.receipt_number}`}
                            >
                              <Link2 className="w-4 h-4 text-[#D99C3B]" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => downloadReceiptPdf(payment)}
                            title="Download PDF" data-testid={`download-pdf-${payment.receipt_number}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#5C635F]">
            Showing {((page - 1) * 25) + 1} - {Math.min(page * 25, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-[#5C635F]">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Allocate leftover money to invoices, straight from the list. Same editor
          and endpoint as Edit > Invoice Mapping, so the money is released and
          re-spread by the one settlement path and can never be credited twice. */}
      <Dialog open={!!allocatePayment} onOpenChange={(open) => { if (!open) setAllocatePayment(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Allocate {allocatePayment ? formatCurrency(unallocatedOf(allocatePayment), allocatePayment.currency) : ''}
              {' '}— {allocatePayment?.receipt_number}
            </DialogTitle>
          </DialogHeader>
          {allocatePayment && (
            <div className="space-y-3">
              <p className="text-sm text-[#5C635F]">
                {allocatePayment.student_name} · receipt {formatCurrency(allocatePayment.amount, allocatePayment.currency)}.
                Tick the invoices this receipt should pay. The whole receipt is re-spread over what you tick,
                oldest term first, and anything left over stays as unallocated credit.
              </p>
              <InvoiceMappingEditor
                paymentId={allocatePayment.id}
                canEdit={isAdmin}
                title="Allocate to invoices"
                onSaved={() => { fetchPayments(); }}
              />
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setAllocatePayment(null)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={!!editPayment} onOpenChange={(open) => { if (!open) setEditPayment(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Payment {editPayment?.receipt_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input type="number" value={editPayData.amount || ''} onChange={(e) => setEditPayData({ ...editPayData, amount: parseFloat(e.target.value) })} min="0" step="0.01" data-testid="edit-payment-amount" />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={editPayData.currency} onValueChange={(v) => setEditPayData({ ...editPayData, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="UGX">UGX</SelectItem>
                    <SelectItem value="RWF">RWF</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Payment Mode</Label>
              <Select value={editPayData.payment_mode} onValueChange={(v) => setEditPayData({ ...editPayData, payment_mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_deposit">Bank Deposit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editPayData.payment_mode === 'bank_deposit' && (
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <BankSelect value={editPayData.bank_name || ''} onChange={(v) => setEditPayData({ ...editPayData, bank_name: v })} testId="edit-payment-bank" />
                </div>
                <div className="space-y-2">
                  <Label>Reference #</Label>
                  <Input value={editPayData.reference_number || ''} onChange={(e) => setEditPayData({ ...editPayData, reference_number: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Effective Date</Label>
                  <Input type="date" value={editPayData.effective_date || ''} onChange={(e) => setEditPayData({ ...editPayData, effective_date: e.target.value })} />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={editPayData.notes || ''} onChange={(e) => setEditPayData({ ...editPayData, notes: e.target.value })} />
            </div>

            {/* Change which invoice(s) this payment is marked against. Unticking an
                invoice releases it (it becomes outstanding again and can be paid by
                another payment); ticking another marks the money against it. */}
            {editPayment && (
              <InvoiceMappingEditor
                paymentId={editPayment.id}
                canEdit={isAdmin}
                expectedAmount={editPayData.amount}
                expectedCurrency={editPayData.currency}
                onBeforeSave={saveEditPayFields}
                onSaved={fetchPayments}
              />
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditPayment(null)}>Cancel</Button>
              <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" onClick={handleEditPaySave} disabled={editPaySubmitting} data-testid="save-edit-payment">
                {editPaySubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentsPage;
